import { useState, useEffect } from "react";
import { http, formatErr } from "../../lib/api";
import { useBrand } from "../../contexts/BrandContext";
import { toast } from "sonner";
import { Palette, UploadCloud, Save, Loader2 } from "lucide-react";

const COLOR_FIELDS = [
  { key: "primary", label: "Rosa antico (primario)" },
  { key: "dark", label: "Prugna scuro" },
  { key: "bg", label: "Fondo chiaro" },
  { key: "accent", label: "Accento pesca" },
];

/**
 * Identità grafica — area riservata al PLATFORM_ADMIN.
 * Permette di cambiare nome visualizzato, colori e logo. Le impostazioni sono
 * persistenti e aggiornano login/titolo/navigazione senza redeploy.
 */
export default function PlatformIdentity() {
  const { brand, refreshBrand } = useBrand();
  const [name, setName] = useState(brand.display_name);
  const [colors, setColors] = useState(brand.colors);
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(brand.display_name);
    setColors(brand.colors);
  }, [brand]);

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Seleziona un'immagine.");
    if (f.size > 2 * 1024 * 1024) return toast.error("Logo troppo grande (max 2 MiB).");
    setLogoFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/brand", { display_name: name, colors });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await http.post("/brand/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await refreshBrand();
      setLogoFile(null);
      setPreview(null);
      toast.success("Identità aggiornata.");
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };

  return (
    <div data-testid="identity-page" className="max-w-3xl mx-auto p-4 md:p-6">
      <div className="mb-6 flex items-center gap-3">
        <Palette className="w-6 h-6" style={{ color: "var(--accent-green)" }} />
        <div>
          <h1 className="font-serif text-3xl">Identità</h1>
          <p className="text-xs text-neutral-500 mt-1 max-w-2xl">
            Nome visualizzato, logo e colori della piattaforma. Modifica solo la presentazione:
            non cambia dominio, URL, progetto Emergent, tenant, database o riferimenti alla matrice.
          </p>
        </div>
      </div>

      <section className="card-clean p-5 mb-5">
        <h2 className="font-serif text-xl mb-3">Nome visualizzato</h2>
        <input value={name} onChange={e => setName(e.target.value)} data-testid="identity-name-input"
          className="w-full px-3 py-2 rounded-md border border-[var(--border-silver)]" maxLength={80} />
      </section>

      <section className="card-clean p-5 mb-5">
        <h2 className="font-serif text-xl mb-3">Logo</h2>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="h-20 w-20 flex items-center justify-center rounded-lg border border-[var(--border-silver)] bg-white overflow-hidden">
            <img src={preview || brand.logo_url} alt="logo" className="max-h-full max-w-full"
              data-testid="identity-logo-preview"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--brand-primary)] text-sm cursor-pointer"
            style={{ color: "var(--brand-dark)" }} data-testid="identity-logo-upload-btn">
            <UploadCloud className="w-4 h-4" />
            {logoFile ? logoFile.name : "Scegli immagine (max 2 MiB)"}
            <input type="file" accept="image/*" className="hidden" onChange={onPickLogo} data-testid="identity-logo-input" />
          </label>
        </div>
      </section>

      <section className="card-clean p-5 mb-5">
        <h2 className="font-serif text-xl mb-3">Colori principali</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {COLOR_FIELDS.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3">
              <input type="color" value={colors?.[key] || "#000000"}
                onChange={e => setColors(c => ({ ...c, [key]: e.target.value }))}
                data-testid={`identity-color-${key}`}
                className="h-10 w-12 rounded border border-[var(--border-silver)] cursor-pointer" />
              <div className="text-sm">
                <div>{label}</div>
                <div className="font-mono text-[11px] text-neutral-500">{colors?.[key]}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <button onClick={save} disabled={busy} data-testid="identity-save-btn"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm disabled:opacity-50"
        style={{ background: "var(--brand-primary)" }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Salva identità
      </button>
    </div>
  );
}
